// Costs (owner ruling of 2026-09-27: "keep an internal history of all llm calls with cost attached so
// admins can simply keep track of day to day costs"). The AI call ledger, read under the audit section:
// four bearings (today, the last seven days, this month, everything), a period and four filters that
// persist in the URL, a by-day table and a breakdown by type, model or person — both fully loaded and
// sorted here — and the calls themselves, paged and sorted by the server over the whole set; each
// table exports as CSV under the same filters. Numbers, identifiers, names and timestamps only. A
// chart is deliberately absent: one that carried the baseline interaction set (04-data §2.2) would be a
// third reading of the by-day table, and one that did not would be incomplete.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { AiCallDto, AiCallSortKey, AiCallTotalsDto, CostsBreakdownRowDto, CostsCallsResponse, CostsDayDto } from '../../shared/api-types.js';
import { AI_CALL_FACET_LABELS, AI_CALL_FACETS, AI_CALL_KIND_LABELS, AI_CALL_OUTCOME_LABELS, type AiCallFacet } from '../../shared/enums.js';
import { AdminSectionStrip } from '../shell/admin.js';
import { useMe } from '../shell/me-context.js';
import {
  Badge,
  Banner,
  Button,
  Chip,
  DataTable,
  Dialog,
  FailureView,
  LinkButton,
  Page,
  PageHead,
  RelativeTime,
  Section,
  SelectField,
  StatTile,
  StatTileRow,
  StatusPill,
  TabPanel,
  Tabs,
  TextField,
  browserTimeZone,
  formatCount,
  formatList,
  formatDateOnly,
  formatUsd,
  inlineFailureText,
  pluralise,
  timeZoneLabel,
  todayDateOnly,
  useIsMobile,
  useRequest,
  useUrlSort,
  useUrlTab,
  sortOptionsFor,
  type DataTableColumn,
  type DataTableStatus,
  type RequestState,
} from '../ui/index.js';
import { adminApi } from './admin-api.js';
import {
  AI_CALL_OUTCOME_TONES,
  COST_BREAKDOWN_CAPTIONS,
  COST_BREAKDOWN_NO_PERSON_NOTE,
  DEFAULT_PERIOD,
  EMPTY_COST_FILTERS,
  PERIOD_PRESETS,
  activeCostFilterCount,
  breakdownHasNoPersonLine,
  breakdownSortValue,
  callFailureLine,
  callLink,
  callNetworkCause,
  callPersonLine,
  callPersonName,
  callPersonNow,
  callsFooterLine,
  costFacets,
  costFilterNarrows,
  costsFilterLine,
  costsQuery,
  daySortValue,
  describeCostFilters,
  nowNamedLine,
  parseCostFilters,
  periodOf,
  promptTokensOf,
  tokensOf,
  utcOffsetMinutes,
  writeCostFilters,
  type BreakdownSortKey,
  type CostFilters,
  type DaySortKey,
} from './costs-helpers.js';
import './admin.css';

const PAGE_SIZE = 100;
const DAY_DEFAULT_SORT = { key: 'day' as DaySortKey, dir: 'desc' as const };
const BREAKDOWN_DEFAULT_SORT = { key: 'cost' as BreakdownSortKey, dir: 'desc' as const };
const CALLS_DEFAULT_SORT = { key: 'occurred_at' as AiCallSortKey, dir: 'desc' as const };

function statusOf<T>(request: RequestState<T>, rowCount: number): DataTableStatus {
  if (request.status === 'loading') return { kind: 'loading', rowCount };
  if (request.status === 'error' && request.error) return { kind: 'error', failure: request.error, onRetry: request.reload };
  return { kind: 'ready' };
}

// Why one title for three places: "Failed" is the label of the `error` outcome alone; the aggregate
// counts every call that did not complete, and the reader must not take the one for the other.
const NOT_COMPLETED_TITLE = 'Invalid output, failed and timed out together';

/** "12 calls · 1 not completed" under a tile's figure — every outcome but Completed, as the tables' column counts it. */
function callsHint(totals: AiCallTotalsDto | undefined): string | undefined {
  if (!totals) return undefined;
  const calls = pluralise(totals.calls, { one: '# call', other: '# calls' });
  return totals.failures > 0 ? `${calls} · ${formatCount(totals.failures)} not completed` : calls;
}

export default function AdminCostsPage() {
  const me = useMe();
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const { filters, dropped } = useMemo(() => parseCostFilters(params), [params]);
  const timeZone = browserTimeZone();
  const zoneLabel = timeZoneLabel(timeZone);
  // Why once per mount: the period and the day cuts must not move under the reader mid-visit.
  const [today] = useState(() => todayDateOnly(new Date(), timeZone));
  const [utcOffset] = useState(() => utcOffsetMinutes());
  const period = useMemo(() => periodOf(filters, today), [filters, today]);
  const [facet, setFacet] = useUrlTab<AiCallFacet>(AI_CALL_FACETS, 'kind', 'facet');
  const [details, setDetails] = useState<AiCallDto | null>(null);

  const setFilters = useCallback((next: CostFilters) => {
    setParams((current) => writeCostFilters(current, next), { replace: true });
  }, [setParams]);

  const filterKey = JSON.stringify({ filters, period });
  // Why apart from `filtering`: the period is the summary line's label, so only the four filters make its calls a match.
  const narrowed = costFilterNarrows(filters);
  const summary = useRequest((signal) => adminApi.costsSummary(costsQuery(filters, { period, utcOffset }), signal), [filterKey, utcOffset]);
  const byDay = useRequest((signal) => adminApi.costsByDay(costsQuery(filters, { period, utcOffset }), signal), [filterKey, utcOffset]);
  const breakdown = useRequest((signal) => adminApi.costsBreakdown(costsQuery(filters, { period, utcOffset, facet }), signal), [filterKey, utcOffset, facet]);

  // --- Calls: the first page through useRequest (cancellation, states); later pages appended. ---
  const callColumns = useCallColumns({ timeZone, zoneLabel, usersView: me.admin.users.view, viewerId: me.subject.id, onDetails: setDetails });
  const [callSort] = useUrlSort<AiCallSortKey>(sortOptionsFor(callColumns, { defaultSort: CALLS_DEFAULT_SORT, namespace: 'calls' }));
  const firstCalls = useRequest<CostsCallsResponse>(
    (signal) => adminApi.costsCalls(costsQuery(filters, { period, utcOffset, sort: callSort, page: { limit: PAGE_SIZE, offset: 0 } }), signal),
    [filterKey, utcOffset, callSort.key, callSort.dir],
  );
  const [moreCalls, setMoreCalls] = useState<AiCallDto[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailure, setMoreFailure] = useState<string | null>(null);
  useEffect(() => { setMoreCalls([]); setMoreFailure(null); }, [filterKey, callSort.key, callSort.dir]);
  const calls = useMemo(() => [...(firstCalls.data?.items ?? []), ...moreCalls], [firstCalls.data, moreCalls]);
  const callsTotal = firstCalls.data?.total ?? 0;
  const loadMore = async (): Promise<void> => {
    setLoadingMore(true);
    setMoreFailure(null);
    const result = await adminApi.costsCalls(costsQuery(filters, { period, utcOffset, sort: callSort, page: { limit: PAGE_SIZE, offset: calls.length } }));
    setLoadingMore(false);
    if (!result.ok) {
      setMoreFailure(inlineFailureText(result));
      return;
    }
    setMoreCalls((current) => [...current, ...result.data.items]);
  };

  const facets = useMemo(() => costFacets(calls, filters), [calls, filters]);
  const retentionDays = summary.data?.retentionDays ?? byDay.data?.retentionDays ?? firstCalls.data?.retentionDays ?? null;
  const filtering = activeCostFilterCount(filters) > 0;
  const clear = () => setFilters({ ...EMPTY_COST_FILTERS });
  const rateCard = summary.data?.rateCard;
  // Why `current` (the Phase 2a review of D29/G8): the previous answer stays on screen while a new filter's request runs,
  // and after it fails; the line labels figures with the live filters only while they are the filters' own answer.
  const summaryLine = costsFilterLine({
    status: summary.status,
    refreshing: summary.refreshing,
    current: summary.current,
    totals: summary.data?.period ?? null,
    unfiltered: summary.data?.periodUnfiltered ?? null,
    period,
    narrowed,
  });
  const exportUrl = (table: 'calls' | 'by-day' | 'breakdown') => adminApi.costsExportUrl(costsQuery(filters, { period, utcOffset, table, facet: table === 'breakdown' ? facet : undefined }));

  // --- By day ---
  const dayColumns = useMemo<DataTableColumn<CostsDayDto>[]>(() => [
    { key: 'day', header: `Day (${zoneLabel})`, name: 'Day', type: 'date', card: 'title', sortValue: (row) => daySortValue(row, 'day'), cell: (row) => formatDateOnly(row.day) },
    { key: 'calls', header: 'Calls', type: 'number', numeric: true, sortValue: (row) => daySortValue(row, 'calls'), cell: (row) => formatCount(row.calls) },
    { key: 'failures', header: 'Not completed', type: 'number', numeric: true, headerTitle: NOT_COMPLETED_TITLE, sortValue: (row) => daySortValue(row, 'failures'), cell: (row) => formatCount(row.failures) },
    { key: 'inputTokens', header: 'Input tokens', type: 'number', numeric: true, headerTitle: 'Prompt tokens, cached reads and writes included', sortValue: (row) => daySortValue(row, 'inputTokens'), cell: (row) => formatCount(promptTokensOf(row)) },
    { key: 'outputTokens', header: 'Output tokens', type: 'number', numeric: true, sortValue: (row) => daySortValue(row, 'outputTokens'), cell: (row) => formatCount(row.outputTokens) },
    { key: 'cost', header: 'Cost (USD)', type: 'number', numeric: true, sortValue: (row) => daySortValue(row, 'cost'), cell: (row) => <CostCell totals={row} /> },
  ], [zoneLabel]);

  // --- Breakdown ---
  const facetLabel = facet === 'kind' ? 'Type' : facet === 'model' ? 'Model' : 'Person';
  const breakdownColumns = useMemo<DataTableColumn<CostsBreakdownRowDto>[]>(() => [
    { key: 'label', header: facetLabel, card: 'title', sortValue: (row) => breakdownSortValue(row, 'label'), cell: (row) => (facet === 'model' ? <code className="selectable">{row.label}</code> : row.label) },
    { key: 'calls', header: 'Calls', type: 'number', numeric: true, sortValue: (row) => breakdownSortValue(row, 'calls'), cell: (row) => formatCount(row.calls) },
    { key: 'failures', header: 'Not completed', type: 'number', numeric: true, headerTitle: NOT_COMPLETED_TITLE, sortValue: (row) => breakdownSortValue(row, 'failures'), cell: (row) => formatCount(row.failures) },
    { key: 'inputTokens', header: 'Input tokens', type: 'number', numeric: true, headerTitle: 'Prompt tokens, cached reads and writes included', sortValue: (row) => breakdownSortValue(row, 'inputTokens'), cell: (row) => formatCount(promptTokensOf(row)) },
    { key: 'outputTokens', header: 'Output tokens', type: 'number', numeric: true, sortValue: (row) => breakdownSortValue(row, 'outputTokens'), cell: (row) => formatCount(row.outputTokens) },
    { key: 'cost', header: 'Cost (USD)', type: 'number', numeric: true, sortValue: (row) => breakdownSortValue(row, 'cost'), cell: (row) => <CostCell totals={row} /> },
  ], [facet, facetLabel]);

  const exportFooter = (table: 'calls' | 'by-day' | 'breakdown', count: string, extra?: ReactNode) => (
    <div className="audit-footer">
      <span>{count}</span>
      <span className="cluster">
        {extra}
        <LinkButton size="sm" variant="secondary" icon="download" href={exportUrl(table)} download>Export CSV</LinkButton>
      </span>
    </div>
  );

  return (
    <Page measure="wide">
      <PageHead title="Costs" strip={<AdminSectionStrip />} />

      {rateCard || retentionDays !== null ? (
        <p className="secondary text-sm" role="status">
          {[
            rateCard ? `Priced by the rate card of ${formatDateOnly(rateCard.version)}.` : null,
            retentionDays === null ? null : `Records are kept for ${pluralise(retentionDays, { one: '# day', other: '# days' })}.`,
          ].filter(Boolean).join(' ')}
        </p>
      ) : null}

      {rateCard && !rateCard.priced && rateCard.model ? (
        <Banner tone="warning" title={`Costs for ${rateCard.model} cannot be computed until the rate card is updated`}>
          Calls are still recorded with their tokens; their cost shows as a dash and is left out of every total until the model is priced.
        </Banner>
      ) : null}

      {dropped.length > 0 ? (
        <Banner tone="warning" title="Some filters in the link were not understood">
          {`Ignored: ${formatList(dropped)}. The other filters still apply.`}
        </Banner>
      ) : null}

      {summary.status === 'error' && summary.error ? (
        <FailureView failure={summary.error} what="the cost totals" onRetry={summary.reload} compact />
      ) : (
        <StatTileRow minWidth="12rem" className="costs-tiles">
          <StatTile label="Today" value={summary.data ? formatUsd(summary.data.windows.today.costMicroUsd) : null} loading={summary.status === 'loading'} hint={callsHint(summary.data?.windows.today)} />
          <StatTile label="Last 7 days" value={summary.data ? formatUsd(summary.data.windows.last7Days.costMicroUsd) : null} loading={summary.status === 'loading'} hint={callsHint(summary.data?.windows.last7Days)} />
          <StatTile label="This month" value={summary.data ? formatUsd(summary.data.windows.thisMonth.costMicroUsd) : null} loading={summary.status === 'loading'} hint={callsHint(summary.data?.windows.thisMonth)} />
          <StatTile label="All time" value={summary.data ? formatUsd(summary.data.windows.allTime.costMicroUsd) : null} loading={summary.status === 'loading'} hint={callsHint(summary.data?.windows.allTime)} />
        </StatTileRow>
      )}

      <CostsFilterBar
        filters={filters}
        facets={facets}
        period={period}
        onChange={setFilters}
        onClear={clear}
        isMobile={isMobile}
        loading={summary.status === 'loading'}
        summaryLine={summaryLine}
      />

      <Section id="by-day" title="By day">
        <DataTable<CostsDayDto, DaySortKey>
          caption="Cost by day"
          failureNoun="the cost by day"
          columns={dayColumns}
          rows={byDay.data?.items ?? []}
          rowKey={(row) => row.day}
          status={statusOf(byDay, 7)}
          sort={{ defaultSort: DAY_DEFAULT_SORT, namespace: 'day' }}
          clientSort
          empty={{ title: 'No AI calls in this period', description: 'Calls appear here as Calis makes them.' }}
          filtered={{ active: filtering, onClear: clear, title: 'No AI calls match these filters' }}
          footer={byDay.data && byDay.data.items.length > 0 ? exportFooter('by-day', pluralise(byDay.data.items.length, { one: '# day', other: '# days' })) : undefined}
        />
      </Section>

      <Section id="breakdown" title="Breakdown">
        {/* Why "Grouping": the tablist names what its tabs choose, a noun as a picker's label is (05-content §3; the owner's
            ruling Q10 of escalation 20261005-200526; Phase 1c R15) — it read "Break down by". */}
        <Tabs<AiCallFacet> id="costs-facet" label="Grouping" tabs={AI_CALL_FACETS.map((id) => ({ id, label: AI_CALL_FACET_LABELS[id] }))} value={facet} onChange={setFacet} />
        <TabPanel tabsId="costs-facet" tabId={facet} active heading={AI_CALL_FACET_LABELS[facet]}>
          <DataTable<CostsBreakdownRowDto, BreakdownSortKey>
            caption={COST_BREAKDOWN_CAPTIONS[facet]}
            failureNoun="the cost breakdown"
            columns={breakdownColumns}
            rows={breakdown.data?.items ?? []}
            rowKey={(row) => row.key ?? 'deleted'}
            status={statusOf(breakdown, 3)}
            sort={{ defaultSort: BREAKDOWN_DEFAULT_SORT, namespace: 'by' }}
            clientSort
            empty={{ title: 'No AI calls in this period', description: 'Calls appear here as Calis makes them.' }}
            filtered={{ active: filtering, onClear: clear, title: 'No AI calls match these filters' }}
            footer={breakdown.data && breakdown.data.items.length > 0 ? exportFooter('breakdown', pluralise(breakdown.data.items.length, { one: '# row', other: '# rows' })) : undefined}
          />
          {/* Why the note (the owner's ruling Q17 of escalation 20261005-200526): the combined line stays — a breakdown groups
              by key, and shared work and deleted accounts have none — and the page says so beside it. */}
          {breakdownHasNoPersonLine(facet, breakdown.data?.items ?? []) ? <p className="text-sm muted">{COST_BREAKDOWN_NO_PERSON_NOTE}</p> : null}
        </TabPanel>
      </Section>

      <Section id="calls" title="Calls">
        <DataTable<AiCallDto, AiCallSortKey>
          caption="AI calls"
          failureNoun="the AI calls"
          columns={callColumns}
          rows={calls}
          rowKey={(row) => row.id}
          status={statusOf(firstCalls, 8)}
          sort={{ defaultSort: CALLS_DEFAULT_SORT, namespace: 'calls' }}
          empty={{ title: 'No AI calls in this period', description: 'Calls appear here as Calis makes them.' }}
          filtered={{ active: filtering, onClear: clear, title: 'No AI calls match these filters', description: describeCostFilters(filters, facets, period).map((chip) => chip.label).join(' · ') }}
          footer={firstCalls.data ? exportFooter(
            'calls',
            // Why only while current: the footer's "matching" describes the rows on screen, which stay the previous
            // answer's while a new filter's request runs (as the summary line); the count waits for its own answer.
            firstCalls.current ? callsFooterLine(calls.length, callsTotal, narrowed) : '',
            <>
              {calls.length < callsTotal ? (
                <Button variant="secondary" size="sm" onClick={() => void loadMore()} loading={loadingMore}>{`Load ${formatCount(Math.min(PAGE_SIZE, callsTotal - calls.length))} more`}</Button>
              ) : null}
              {moreFailure ? <span role="alert">{moreFailure}</span> : null}
            </>,
          ) : undefined}
        />
      </Section>

      <Dialog open={details !== null} onClose={() => setDetails(null)} title="Call details" size="md">
        {details ? <CallDetails call={details} timeZone={timeZone} /> : null}
      </Dialog>
    </Page>
  );
}

/** A cost with its unpriced calls named, so a total that leaves some out says so where it is read. */
function CostCell({ totals }: { totals: AiCallTotalsDto }) {
  if (totals.unpricedCalls === 0) return <>{formatUsd(totals.costMicroUsd)}</>;
  const note = `${pluralise(totals.unpricedCalls, { one: '# call', other: '# calls' })} without a price left out`;
  return <span title={note}>{formatUsd(totals.costMicroUsd)}<span className="visually-hidden">{` (${note})`}</span><span aria-hidden="true">*</span></span>;
}

// --- Calls table columns --------------------------------------------------------------------------

interface CallColumnOptions {
  timeZone: string;
  zoneLabel: string;
  usersView: boolean;
  viewerId: string;
  onDetails: (call: AiCallDto) => void;
}

function useCallColumns({ timeZone, zoneLabel, usersView, viewerId, onDetails }: CallColumnOptions): DataTableColumn<AiCallDto>[] {
  // Folding (04-data §1.7; ADR 0168): the call's particulars (Effort, Model, Tokens — the split is in the details) first,
  // then its Outcome, then When and Person, the columns a call is compared by. The type names the row, the cost is what
  // the page answers, and the row's links are its actions: those three never fold.
  return useMemo<DataTableColumn<AiCallDto>[]>(() => [
    { key: 'occurred_at', header: `When (${zoneLabel})`, name: 'When', type: 'date', card: 'meta', fold: 5, cell: (row) => <RelativeTime value={row.occurredAt} mode="absolute" timeZone={timeZone} /> },
    {
      key: 'person',
      header: 'Person',
      fold: 6,
      type: 'text',
      // Why it sorts, and on what (design system 9.0.0 04-data §1.2; ADR 0167; migration 0051): each row stores the
      // person's name as it was when the call was recorded, and the server orders the whole set by that stored name —
      // the value this cell shows. The link and the address beneath come from the person's record now, by the key — and
      // where the roster names them differently today, that name too, in words beneath the stored one, on the table and
      // the card alike (04-data §1.2; `callPersonNow`): the header's title says the rule, but only to a pointer that hovers.
      headerTitle: 'The name when the call was recorded; a later rename does not change it',
      cell: (row) => {
        if (!row.person) return <span className="muted">{callPersonName(row)}</span>;
        const now = callPersonNow(row);
        return (
          <span className="audit-actor">
            {usersView ? <Link to={`/admin/users/${encodeURIComponent(row.person.id)}`} className="hit-area">{callPersonName(row)}</Link> : <span>{callPersonName(row)}</span>}
            {now === null ? null : <span className="audit-actor__now">{nowNamedLine(now)}</span>}
            <span className="audit-actor__email">{row.person.email}</span>
          </span>
        );
      },
    },
    { key: 'kind', header: 'Type', type: 'enum', card: 'title', cell: (row) => AI_CALL_KIND_LABELS[row.kind] },
    { key: 'model', header: 'Model', fold: 2, cell: (row) => <code className="selectable text-xs">{row.model}</code> },
    { key: 'effort', header: 'Effort', type: 'enum', fold: 1, cell: (row) => row.effort ?? '—' },
    {
      key: 'tokens',
      header: 'Tokens',
      fold: 3,
      type: 'number',
      numeric: true,
      headerTitle: 'Prompt and answer together; the split is in the details',
      cell: (row) => {
        const total = tokensOf(row);
        return total === null ? '—' : <span title={`${formatCount((row.inputTokens ?? 0) + (row.cacheReadTokens ?? 0) + (row.cacheCreationTokens ?? 0))} in, ${formatCount(row.outputTokens ?? 0)} out`}>{formatCount(total)}</span>;
      },
    },
    { key: 'cost', header: 'Cost (USD)', type: 'number', numeric: true, cell: (row) => (row.costMicroUsd === null ? <span title="No price on the rate card for this model">—</span> : formatUsd(row.costMicroUsd)) },
    { key: 'outcome', header: 'Outcome', type: 'enum', fold: 4, cell: (row) => <StatusPill tone={AI_CALL_OUTCOME_TONES[row.outcome]}>{AI_CALL_OUTCOME_LABELS[row.outcome]}</StatusPill> },
    {
      key: 'link',
      header: <span className="visually-hidden">Open</span>,
      name: 'Open',
      sortable: false,
      align: 'end',
      card: 'actions',
      cell: (row) => {
        const link = callLink(row, viewerId);
        return (
          <span className="row-actions">
            {link ? <LinkButton size="sm" variant="ghost" to={link.to}>{link.label}</LinkButton> : null}
            <Button size="sm" variant="ghost" onClick={() => onDetails(row)}>Details</Button>
          </span>
        );
      },
    },
  ], [timeZone, zoneLabel, usersView, viewerId, onDetails]);
}

// --- Filter bar -----------------------------------------------------------------------------------

interface CostsFilterBarProps {
  filters: CostFilters;
  facets: ReturnType<typeof costFacets>;
  period: ReturnType<typeof periodOf>;
  onChange: (next: CostFilters) => void;
  onClear: () => void;
  isMobile: boolean;
  loading: boolean;
  /** The live line (`costsFilterLine`): the held answer's figures under its own labels, a loading word, or nothing. */
  summaryLine: string;
}

const CUSTOM_PERIOD = 'custom';

/**
 * Structured filters only, as the audit log has: the period as a preset or two dates, and four closed
 * or loaded vocabularies. Applied on change on desktop; below --bp-md they live in a sheet with an
 * explicit Apply, the chip rail staying outside (03-components §10; 04-data §1.5).
 */
function CostsFilterBar({ filters, facets, period, onChange, onClear, isMobile, loading, summaryLine }: CostsFilterBarProps) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [draft, setDraft] = useState<CostFilters>(filters);
  const periodTimer = useRef<number | null>(null);
  useEffect(() => () => { if (periodTimer.current !== null) window.clearTimeout(periodTimer.current); }, []);

  const activeCount = activeCostFilterCount(filters);
  const chips = describeCostFilters(filters, facets, period);
  const remove = (key: string): void => {
    if (key === 'period') onChange({ ...filters, days: '', from: '', to: '' });
    else if (key in filters) onChange({ ...filters, [key]: '' });
  };

  const fields = (value: CostFilters, set: (next: CostFilters) => void, immediate: boolean) => {
    const custom = value.from !== '' || value.to !== '';
    const presetValue = custom ? CUSTOM_PERIOD : value.days === '' ? DEFAULT_PERIOD : value.days;
    // Why: a date field fires change on every keystroke in some browsers; the period waits ~300 ms
    // before it refetches so a half-typed year does not produce a queue of results.
    const setPeriod = (next: CostFilters): void => {
      if (!immediate) { set(next); return; }
      if (periodTimer.current !== null) window.clearTimeout(periodTimer.current);
      periodTimer.current = window.setTimeout(() => set(next), 300);
    };
    const choosePreset = (chosen: string): void => {
      if (chosen === CUSTOM_PERIOD) {
        // Custom starts from the preset's own bounds, so nothing jumps until a date is changed.
        set({ ...value, days: '', from: period.from, to: period.to });
      } else {
        set({ ...value, days: chosen === DEFAULT_PERIOD ? '' : (chosen as CostFilters['days']), from: '', to: '' });
      }
    };
    return (
      <>
        <SelectField
          className="filter-bar__facet"
          label="Period"
          value={presetValue}
          onChange={(event) => choosePreset(event.target.value)}
          options={[...PERIOD_PRESETS.map((days) => ({ value: days, label: `Last ${days} days` })), { value: CUSTOM_PERIOD, label: 'Custom dates' }]}
          disabled={loading}
        />
        {custom ? (
          <>
            <TextField className="filter-bar__period" label="From" type="date" value={value.from} max={value.to || undefined} onChange={(event) => setPeriod({ ...value, days: '', from: event.target.value })} disabled={loading} />
            <TextField className="filter-bar__period" label="Until" type="date" value={value.to} min={value.from || undefined} onChange={(event) => setPeriod({ ...value, days: '', to: event.target.value })} disabled={loading} />
          </>
        ) : null}
        <SelectField className="filter-bar__facet" label="Type" value={value.kind} onChange={(event) => set({ ...value, kind: event.target.value })} options={facets.kinds} placeholderOption="Any type" disabled={loading} />
        <SelectField className="filter-bar__facet" label="Model" value={value.model} onChange={(event) => set({ ...value, model: event.target.value })} options={facets.models} placeholderOption="Any model" disabled={loading} />
        <SelectField className="filter-bar__facet" label="Person" value={value.person} onChange={(event) => set({ ...value, person: event.target.value })} options={facets.people} placeholderOption="Anyone" disabled={loading} />
        <SelectField className="filter-bar__facet" label="Outcome" value={value.outcome} onChange={(event) => set({ ...value, outcome: event.target.value })} options={facets.outcomes} placeholderOption="Any outcome" disabled={loading} />
      </>
    );
  };

  return (
    <div className="filter-bar">
      <div className="filter-bar__controls">
        {isMobile ? (
          <Button className="filter-bar__trigger" variant="secondary" icon="filter" onClick={() => { setDraft(filters); setSheetOpen(true); }} aria-label={activeCount > 0 ? `Filters, ${activeCount} active` : 'Filters'}>
            Filters{activeCount > 0 ? <> <Badge tone="accent">{String(activeCount)}</Badge></> : null}
          </Button>
        ) : fields(filters, onChange, true)}
      </div>
      <div className="filter-bar__active">
        <p className="filter-bar__count" role="status" aria-live="polite">{summaryLine}</p>
        {chips.length > 0 ? (
          <ul className="cluster" role="list" aria-label="Active filters">
            {chips.map((chip) => (
              <li key={chip.key}>
                <Chip selected onRemove={() => remove(chip.key)} removeLabel={`Remove filter: ${chip.label}`}>{chip.label}</Chip>
              </li>
            ))}
            <li>
              <Button variant="link" size="sm" onClick={onClear}>Clear all</Button>
            </li>
          </ul>
        ) : null}
      </div>
      {isMobile ? (
        <Dialog
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title="Filters"
          size="sm"
          initialFocus="first"
          actions={
            <>
              <Button variant="secondary" onClick={() => setSheetOpen(false)}>Cancel</Button>
              <Button variant="primary" onClick={() => { onChange(draft); setSheetOpen(false); }}>Apply</Button>
            </>
          }
        >
          <div className="stack stack--3">{fields(draft, setDraft, false)}</div>
        </Dialog>
      ) : null}
    </div>
  );
}

// --- Call details ---------------------------------------------------------------------------------

function CallDetails({ call, timeZone }: { call: AiCallDto; timeZone: string }) {
  const count = (value: number | null): string => (value === null ? '—' : formatCount(value));
  const failure = callFailureLine(call);
  return (
    <dl className="field-list">
      <dt>When</dt>
      <dd><RelativeTime value={call.occurredAt} mode="absolute" timeZone={timeZone} /></dd>
      <dt>Person</dt>
      <dd>{callPersonLine(call)}</dd>
      <dt>Type</dt>
      <dd>{AI_CALL_KIND_LABELS[call.kind]}</dd>
      <dt>Provider and model</dt>
      <dd>{call.provider} · <code className="selectable text-xs">{call.model}</code>{call.effort ? ` · effort ${call.effort}` : ''}</dd>
      <dt>Outcome</dt>
      <dd><StatusPill tone={AI_CALL_OUTCOME_TONES[call.outcome]}>{AI_CALL_OUTCOME_LABELS[call.outcome]}</StatusPill>{call.errorCode ? <> <code className="selectable text-xs">{call.errorCode}</code></> : null}{failure ? <span className="text-xs">{` · ${failure}`}</span> : null}{call.stopReason ? <span className="text-xs muted">{` · stopped: ${call.stopReason}`}</span> : null}</dd>
      {/* Why not for a call no answer reached: its words are the network's, already on the Outcome line. */}
      {call.errorMessage && callNetworkCause(call) === null ? (
        <>
          {/* The API's own words, sanitised on the server (quoted values replaced by ‹…›): the reason to act on. */}
          <dt>Reason</dt>
          <dd><code className="selectable text-xs">{call.errorMessage}</code></dd>
        </>
      ) : null}
      <dt>Tokens</dt>
      <dd>{tokensOf(call) === null ? '—' : `${count(call.inputTokens)} in (uncached) · ${count(call.cacheReadTokens)} cache read · ${count(call.cacheCreationTokens)} cache write · ${count(call.outputTokens)} out`}</dd>
      <dt>Cost</dt>
      {/* Why whole alternatives (05-content §8; the Phase 1c audit's D30): the rate card's parenthesis was spliced on. */}
      <dd>{call.costMicroUsd === null ? 'No price on the rate card for this model' : call.rateCardVersion ? `${formatUsd(call.costMicroUsd)} (rate card of ${formatDateOnly(call.rateCardVersion)})` : formatUsd(call.costMicroUsd)}</dd>
      <dt>Duration</dt>
      <dd>{call.durationMs === null ? '—' : `${formatCount(Math.round(call.durationMs / 100) / 10)} s`}</dd>
      <dt>Identifiers</dt>
      <dd className="text-xs">
        {`call ${call.id}`}
        {call.jobId ? ` · job ${call.jobId}` : ''}
        {/* Why the target is left out when it is the meeting: a summary's target is its meeting, and one id need not print twice. */}
        {call.targetType && call.targetId && !(call.targetType === 'meeting' && call.targetId === call.meetingId) ? ` · ${call.targetType} ${call.targetId}` : ''}
        {call.meetingId ? ` · meeting ${call.meetingId}` : ''}
        {call.requestId ? ` · request ${call.requestId}` : ''}
      </dd>
    </dl>
  );
}
